import { faTimes, faWindowMaximize, faWindowRestore } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconButton, Tooltip, Typography } from "@mui/material"
import { appEventModule } from "app/event"
import { translate } from "app/language/service"
import { ILocation } from "geometry/model"
import React from "react"
import { HandleStyles, Rnd } from "react-rnd"
import { FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS, FLOATING_PANEL_HEADER_HEIGHT_IN_REM } from "ui/model"
import { FlexBox } from "./FlexBox"

export interface JFloatingPanelProps {
  initialPosition: ILocation // x/y on the screen, not lat/lon on map
  initialWidth: string
  initialHeight: string
  minWidth?: string
  minHeight?: string
  maxWidth?: string
  maxHeight?: string
  title: string
  resizable?: boolean
  initialMaximized?: boolean
  hideBorder?: boolean
  id?: string
  onClose(): any
  onResize?(width: number, height: number, isMaximized: boolean): any
  onMoveStop?(position: ILocation): any
}

export interface JFloatingPanelState {
  position: ILocation
  width: string
  height: string
  isMaximized: boolean
  resizeRenderIndex: number
}

const handleStyles: HandleStyles = {
  top: {
    top: 0,
    position: "absolute",
    cursor: "row-resize",
    userSelect: "none",
    height: `${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    left: `-${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    width: `calc(100% + ${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    marginTop: `-${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`
  },
  bottom: {
    bottom: 0,
    position: "absolute",
    cursor: "row-resize",
    userSelect: "none",
    height: `${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    left: `-${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    width: `calc(100% + ${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    marginBottom: `-${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`
  },
  left: {
    position: "absolute",
    left: 0,
    cursor: "col-resize",
    userSelect: "none",
    width: `${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    top: `-${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    height: `calc(100% + ${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    marginLeft: `-${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`
  },
  right: {
    position: "absolute",
    right: 0,
    cursor: "col-resize",
    userSelect: "none",
    width: `${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    top: `-${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    height: `calc(100% + ${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    marginRight: `-${FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`
  },
  bottomRight: {
    position: "absolute",
    userSelect: "none",
    cursor: "se-resize",
    width: `${3 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    height: `${3 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    right: `-${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    bottom: `-${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`
  },
  bottomLeft: {
    position: "absolute",
    userSelect: "none",
    cursor: "sw-resize",
    width: `${3 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    height: `${3 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    left: `-${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    bottom: `-${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`
  },
  topLeft: {
    position: "absolute",
    userSelect: "none",
    cursor: "nw-resize",
    width: `${3 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    height: `${3 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    left: `-${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    top: `-${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`
  },
  topRight: {
    position: "absolute",
    userSelect: "none",
    cursor: "ne-resize",
    width: `${3 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    height: `${3 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    right: `-${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`,
    top: `-${2 * FLOATING_PANEL_BORDER_WIDTH_IN_PIXELS}px`
  }
}

export class FloatingPanel extends React.PureComponent<JFloatingPanelProps, JFloatingPanelState> {
  private static nextPanelId: number = 0

  private panelId: number = FloatingPanel.nextPanelId++

  public constructor(props: JFloatingPanelProps) {
    super(props)
    this.state = {
      position: props.initialPosition,
      width: props.initialWidth,
      height: props.initialHeight,
      isMaximized: props.initialMaximized || false,
      resizeRenderIndex: 0 // used to trigger a render after a window resize
    }
    appEventModule.on.windowResize(this.getResizeEventId(), () => {
      this.setState({
        resizeRenderIndex: this.state.resizeRenderIndex + 1
      })
    })
  }

  private static getSafeWidth(newWidth: string): string {
    let width = Number(newWidth.replace("px", ""))
    if (document.body.offsetWidth < width) {
      width = Math.max(document.body.offsetWidth - 50, 100)
    }
    return `${width}px`
  }

  private static getSafeHeight(newHeight: string): string {
    let height = Number(newHeight.replace("px", ""))
    if (document.body.offsetHeight < height) {
      height = Math.max(document.body.offsetHeight - 50, 100)
    }
    return `${height}px`
  }

  private static getSafePosition(newPosition: ILocation): ILocation {
    const safePositionInPixel: ILocation = { ...newPosition }
    if (safePositionInPixel.x < 0) {
      safePositionInPixel.x = 0
    } else if (safePositionInPixel.x > document.body.offsetWidth) {
      safePositionInPixel.x = document.body.offsetWidth - 100
    }
    if (safePositionInPixel.y < 0) {
      safePositionInPixel.y = 0
    } else if (safePositionInPixel.y > document.body.offsetHeight) {
      safePositionInPixel.y = document.body.offsetHeight - 100
    }
    return safePositionInPixel
  }

  public componentWillUnmount(): void {
    appEventModule.remove(this.getResizeEventId())
  }

  public componentDidUpdate(prevProps: JFloatingPanelProps) {
    let newState: Partial<JFloatingPanelState> | undefined
    if (this.props.initialWidth !== prevProps.initialWidth) {
      newState = { width: this.props.initialWidth }
    }
    if (this.props.initialHeight !== prevProps.initialHeight) {
      if (newState) {
        newState.height = this.props.initialHeight
      } else {
        newState = { height: this.props.initialHeight }
      }
    }
    if (
      this.props.initialPosition.x !== prevProps.initialPosition.x ||
      this.props.initialPosition.y !== prevProps.initialPosition.y
    ) {
      if (newState) {
        newState.position = this.props.initialPosition
      } else {
        newState = { position: this.props.initialPosition }
      }
    }
    if (newState !== undefined) {
      this.setState(newState as any)
    }
  }

  public render() {
    const width = FloatingPanel.getSafeWidth(this.state.width)
    const height = FloatingPanel.getSafeHeight(this.state.height)
    const position = FloatingPanel.getSafePosition(this.state.position)
    return (
      <Rnd
        size={{
          width: this.state.isMaximized ? this.getMaximizedWidth() : width,
          height: this.state.isMaximized ? this.getMaximizedHeight() : height
        }}
        enableResizing={!this.state.isMaximized && this.props.resizable === true}
        position={this.state.isMaximized ? { x: 0, y: 0 } : position}
        className={`floating-panel-root${this.props.hideBorder ? "" : " has-border"}`}
        bounds="window"
        dragHandleClassName="floating-panel-header-title"
        resizeHandleStyles={handleStyles}
        minWidth={this.props.minWidth}
        maxWidth={this.props.maxWidth}
        minHeight={this.props.minHeight}
        maxHeight={this.props.maxHeight}
        onDragStop={(e, d) => {
          if (this.state.isMaximized) {
            return
          }
          const newPosition: ILocation = { x: d.x, y: d.y }
          this.setState({ position: newPosition })
          if (this.props.onMoveStop) {
            this.props.onMoveStop(newPosition)
          }
        }}
        onResize={(e, direction, ref, delta, newPosition) => {
          this.setState({ position: newPosition })
          if (ref.style.width !== width) {
            this.setState({ width: ref.style.width })
          }
          if (ref.style.height !== height) {
            this.setState({ height: ref.style.height })
          }
          if (this.props.onResize) {
            this.props.onResize(
              Number(ref.style.width.replace("px", "")), // width ex: "1892px", we remove px at the end
              Number(ref.style.height.replace("px", "")),
              false
            )
          }
        }}>
        <FlexBox
          className="floating-panel-header"
          jc="start"
          sx={{
            cursor: this.props.resizable ? "pointer" : "move",
            top: 0,
            left: 0,
            width: "100%",
            borderTopLeftRadius: ".1875rem",
            borderTopRightRadius: ".1875rem",
            height: FLOATING_PANEL_HEADER_HEIGHT_IN_REM,
            backgroundColor: theme => theme.palette.background.paper
          }}
          onDoubleClick={!this.props.resizable ? undefined : () => this.toggleMaximized()}>
          <Typography
            className="floating-panel-header-title"
            color="textPrimary"
            sx={{
              fontSize: "1rem",
              p: ".5rem",
              pl: "1rem",
              pr: "1rem",
              width: "100%",
              maxWidth: "calc(100% - 4rem)"
            }}>
            {this.props.title}
          </Typography>
          {this.props.resizable && (
            <Tooltip
              title={translate(this.state.isMaximized ? "button.window.restore" : "button.window.maximized")}
              placement="top">
              <IconButton
                size="small"
                sx={{
                  "position": "absolute",
                  "top": ".2rem",
                  "right": "2.2rem",
                  "& .floating-panel-header-maximized": {
                    "fontSize": "1rem",
                    "color": theme => theme.palette.text.secondary,
                    "&:hover": {
                      color: theme => theme.palette.text.primary
                    }
                  }
                }}
                onClick={event => {
                  event.stopPropagation()
                  event.preventDefault()
                  this.toggleMaximized()
                }}>
                <FontAwesomeIcon
                  className="floating-panel-header-maximized"
                  icon={this.state.isMaximized ? faWindowRestore : faWindowMaximize}
                />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={translate("button.close")} placement="top">
            <IconButton
              size="small"
              sx={{
                "position": "absolute",
                "top": ".2rem",
                "right": ".5rem",
                "& .floating-panel-header-close": {
                  "fontSize": "1rem",
                  "color": theme => theme.palette.text.secondary,
                  "&:hover": {
                    color: theme => theme.palette.text.primary
                  }
                }
              }}
              onClick={event => {
                event.stopPropagation()
                event.preventDefault()
                this.props.onClose()
              }}>
              <FontAwesomeIcon className="floating-panel-header-close" icon={faTimes} />
            </IconButton>
          </Tooltip>
        </FlexBox>
        <FlexBox
          id={this.props.id}
          sx={{
            width: "100%",
            height: `calc(100% - ${FLOATING_PANEL_HEADER_HEIGHT_IN_REM})`
          }}>
          {this.props.children}
        </FlexBox>
      </Rnd>
    )
  }

  private getResizeEventId(): string {
    return `floating-panel-resize-${this.panelId}`
  }

  private getMaximizedWidth(): number {
    return document.body.offsetWidth
  }

  private getMaximizedHeight(): number {
    return document.body.offsetHeight
  }

  private toggleMaximized(): void {
    const isMaximized = !this.state.isMaximized
    this.setState({ isMaximized })
    if (this.props.onResize) {
      if (isMaximized) {
        this.props.onResize(this.getMaximizedWidth(), this.getMaximizedHeight(), true)
      } else {
        this.props.onResize(
          Number(this.state.width.replace("px", "")),
          Number(this.state.height.replace("px", "")),
          false
        )
      }
    }
  }
}
